/**
 * MentionSuggestions — the `@` autocomplete list above the comment composer.
 *
 * Presentation only: the composer owns the query, the active index and the
 * keyboard handling, because those live with the input this listbox describes.
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { MentionSuggestion } from '../../utils/mentionSuggestions';
import { avatarInitial, avatarTone } from '../../components/ui/avatarFallback';
import '../../components/ui/avatar.css';
import './feedModal.css';

interface MentionSuggestionsProps {
  suggestions: readonly MentionSuggestion[];
  activeIndex: number;
  /** Matches the composer input's `aria-controls`. */
  listboxId: string;
  optionId: (index: number) => string;
  onSelect: (suggestion: MentionSuggestion) => void;
  onActiveIndexChange: (index: number) => void;
}

export function MentionSuggestions({
  suggestions,
  activeIndex,
  listboxId,
  optionId,
  onSelect,
  onActiveIndexChange,
}: MentionSuggestionsProps) {
  const { t } = useTranslation();

  if (suggestions.length === 0) return null;

  return (
    <ul
      className="mention-suggestions"
      id={listboxId}
      role="listbox"
      aria-label={t('comments.accessibility.mentionSuggestions')}
    >
      {suggestions.map((suggestion, index) => (
        <li key={suggestion.userGuid} role="none">
          <button
            className={`mention-suggestions__item${index === activeIndex ? ' mention-suggestions__item--active' : ''}`}
            type="button"
            id={optionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            tabIndex={-1}
            // Selecting must not blur the composer first, or the caret is lost.
            onMouseDown={event => event.preventDefault()}
            onMouseEnter={() => onActiveIndexChange(index)}
            onClick={() => onSelect(suggestion)}
          >
            {suggestion.avatar ? (
              <img className="mention-suggestions__avatar" src={suggestion.avatar} alt="" />
            ) : (
              <span
                className="mention-suggestions__avatar mention-suggestions__avatar--initials"
                data-avatar-tone={avatarTone(suggestion.userGuid)}
                aria-hidden
              >
                {avatarInitial(suggestion.displayName)}
              </span>
            )}
            <span className="mention-suggestions__text">
              <span className="mention-suggestions__name">{suggestion.displayName}</span>
              {/* Exactly what the mention will read once inserted. */}
              <span className="mention-suggestions__username">@{suggestion.token}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
